import { Alert, Button, Form, Input, Radio, Select, Skeleton } from 'antd';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Calendar } from 'components/Calendar';
import FulfillmentType from 'components/FulfillmentType';
import StoreDetails from 'components/StoreDetails';
import { useApi } from 'lib/ApiContext';
import { submitDonation } from 'lib/requests';
import fulfillmentTypes from 'lib/fulfillmentTypes';
import { DEFAULT_STORE } from 'lib/constants';

export default function Donation() {
  const { t, i18n } = useTranslation();

  const bikeSizeOptions = useMemo(
    () => [
      {
        label: t('donation.bikeSize.options.loopfiets'),
        value: 'Loopfiets'
      },
      {
        label: t('donation.bikeSize.options.child22'),
        value: 'Kinderfiets t/m 22 inch'
      },
      {
        label: t('donation.bikeSize.options.size24'),
        value: 'Fiets vanaf 24 inch'
      }
    ],
    [t, i18n.language]
  );

  const quantityOptions = useMemo(
    () => [
      ...Array.from({ length: 15 }, (_, i) => ({
        label: String(i + 1),
        value: i + 1
      })),
      {
        label: t('donation.quantity.moreThan15'),
        value: 'more_than_15'
      }
    ],
    [t, i18n.language]
  );

  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [postalCode, setPostalCode] = useState('');
  const [currentDatetime, setCurrentDatetime] = useState();
  const [fulfillmentType, setFulfillmentType] = useState();
  const [pickupLocation, setPickupLocation] = useState(DEFAULT_STORE);

  const {
    settings: serviceSettings,
    postcodes: allPostalCodes,
    isWithinServiceArea
  } = useApi();

  const isInRegion = isWithinServiceArea(postalCode);

  function onDatetimeChange(d) {
    setCurrentDatetime(d);
    form.setFieldsValue({ date: d });
  }

  function onFulfillmentTypeChange(value) {
    if (fulfillmentType !== value && currentDatetime) {
      const [dateString] = currentDatetime.split(' ');
      setCurrentDatetime(dateString);
      form.setFieldsValue({ date: undefined });
    }
    setFulfillmentType(value);
  }

  async function onFinish(values) {
    setSubmitting(true);
    try {
      await submitDonation({
        email: values.email,
        phone: values.phone,
        first_name: values.first_name,
        last_name: values.last_name,
        date: values.date,
        address: values.address,
        house_number: values.house_number,
        postal_code: values.postal_code,
        city: values.city,
        bike_size: values.bike_size,
        quantity: values.quantity,
        fulfillment_type: fulfillmentType,
        pickup_location: pickupLocation,
        locale: i18n.language
      });
      setSubmitted(true);
      window.parent.postMessage(
        { type: 'donation-submitted', email: values.email },
        '*'
      );
    } catch {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <h2 className="text-xl font-semibold mb-2">
          {t('donation.thankYou.title')}
        </h2>
        <p className="text-gray-600">{t('donation.thankYou.description')}</p>
      </div>
    );
  }

  if (!serviceSettings || !allPostalCodes) {
    return <Skeleton active paragraph={{ rows: 8 }} />;
  }

  return (
    <div className="">
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        requiredMark={(label, { required }) => (
          <>
            {label}
            {required ? (
              <span
                className="ms-1 text-[var(--ant-color-error)] text-red-500"
                aria-hidden>
                *
              </span>
            ) : null}
          </>
        )}
        initialValues={{ quantity: 1 }}
        className="grid grid-cols-6 gap-x-4">
        <Form.Item
          name="first_name"
          label={t('donation.fields.firstName.label')}
          className="col-span-6 md:col-span-3"
          rules={[
            { required: true, message: t('donation.fields.firstName.required') }
          ]}>
          <Input placeholder={t('donation.fields.firstName.placeholder')} />
        </Form.Item>

        <Form.Item
          name="last_name"
          label={t('donation.fields.lastName.label')}
          className="col-span-6 md:col-span-3"
          rules={[
            { required: true, message: t('donation.fields.lastName.required') }
          ]}>
          <Input placeholder={t('donation.fields.lastName.placeholder')} />
        </Form.Item>

        <Form.Item
          name="email"
          label={t('donation.fields.email.label')}
          className="col-span-6 md:col-span-3"
          validateTrigger="onBlur"
          rules={[
            { required: true, message: t('donation.fields.email.required') },
            { type: 'email', message: t('donation.fields.email.invalid') }
          ]}>
          <Input placeholder={t('donation.fields.email.placeholder')} />
        </Form.Item>

        <Form.Item
          name="phone"
          label={t('donation.fields.phone.label')}
          className="col-span-6 md:col-span-3"
          rules={[
            { required: true, message: t('donation.fields.phone.required') }
          ]}>
          <Input placeholder={t('donation.fields.phone.placeholder')} />
        </Form.Item>

        <Form.Item
          name="address"
          label={t('donation.fields.street.label')}
          className="col-span-6 md:col-span-4"
          rules={[
            { required: true, message: t('donation.fields.street.required') }
          ]}>
          <Input placeholder={t('donation.fields.street.placeholder')} />
        </Form.Item>

        <Form.Item
          name="house_number"
          label={t('donation.fields.houseNumber.label')}
          className="col-span-6 md:col-span-2"
          rules={[
            {
              required: true,
              message: t('donation.fields.houseNumber.required')
            }
          ]}>
          <Input placeholder={t('donation.fields.houseNumber.placeholder')} />
        </Form.Item>

        <Form.Item
          name="postal_code"
          label={t('donation.fields.postalCode.label')}
          className="col-span-6 md:col-span-3"
          rules={[
            {
              required: true,
              message: t('donation.fields.postalCode.required')
            }
          ]}>
          <Input
            placeholder={t('donation.fields.postalCode.placeholder')}
            onChange={(e) => {
              if (e.target.value.length < 4) return;
              setPostalCode(e.target.value);
              setFulfillmentType(fulfillmentTypes.PICKUP);
            }}
          />
        </Form.Item>

        <Form.Item
          name="city"
          label={t('donation.fields.city.label')}
          className="col-span-6 md:col-span-3"
          rules={[
            { required: true, message: t('donation.fields.city.required') }
          ]}>
          <Input placeholder={t('donation.fields.city.placeholder')} />
        </Form.Item>

        <Form.Item
          name="bike_size"
          label={t('donation.bikeSize.label')}
          className="col-span-6 md:col-span-3"
          rules={[
            {
              required: true,
              message: t('donation.bikeSize.required')
            }
          ]}>
          <Select
            placeholder={t('donation.bikeSize.placeholder')}
            options={bikeSizeOptions}
          />
        </Form.Item>

        <Form.Item
          name="quantity"
          label={t('donation.quantity.label')}
          className="col-span-6 md:col-span-3"
          rules={[
            { required: true, message: t('donation.quantity.required') }
          ]}>
          <Select options={quantityOptions} />
        </Form.Item>

        <div className="col-span-6 mb-5">
          <Form.Item
            name="date"
            label={t('donation.pickupDate.label')}
            className="hidden"
            rules={[
              { required: true, message: t('donation.pickupDate.required') }
            ]}>
            <Input type="hidden" />
          </Form.Item>

          {postalCode.length >= 4 && (
            <div
              className="my-4 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4"
              id="pickupOrDeliveryDiv">
              <FulfillmentType
                id={fulfillmentTypes.DELIVERY}
                title={t('steps.deliveryMoment.fulfillmentType.pickup.title')}
                description={t(
                  'steps.deliveryMoment.fulfillmentType.pickup.donation_description'
                )}
                onOptionChange={onFulfillmentTypeChange}
                value={fulfillmentType}
                disabled={!isInRegion}
              />
              <FulfillmentType
                id={fulfillmentTypes.PICKUP}
                title={t('steps.deliveryMoment.fulfillmentType.dropoff.title')}
                description={t(
                  'steps.deliveryMoment.fulfillmentType.dropoff.description'
                )}
                onOptionChange={onFulfillmentTypeChange}
                value={fulfillmentType}
              />
            </div>
          )}

          {fulfillmentType === fulfillmentTypes.PICKUP && (
            <div className="my-5">
              <Radio.Group
                value={pickupLocation}
                options={Object.keys(serviceSettings.stores).map((key) => ({
                  label: key,
                  value: key
                }))}
                optionType="button"
                size="large"
                onChange={(e) => {
                  setPickupLocation(e.target.value);
                  setCurrentDatetime(undefined);
                  form.setFieldsValue({ date: undefined });
                }}
              />
            </div>
          )}

          {pickupLocation && fulfillmentType === fulfillmentTypes.PICKUP && (
            <StoreDetails name={pickupLocation} />
          )}

          {fulfillmentType &&
            pickupLocation &&
            (isInRegion || fulfillmentType === fulfillmentTypes.PICKUP) && (
              <div id="calendar" className="mt-5">
                <Calendar
                  postcodes={allPostalCodes}
                  serviceSettings={serviceSettings}
                  fulfillmentType={fulfillmentType}
                  postalCode={postalCode}
                  onDatetimeChange={onDatetimeChange}
                  value={currentDatetime}
                  store={serviceSettings.stores[pickupLocation]}
                  hideNextButton={true}
                  pickupLocation={pickupLocation}
                  fulfillmentLabel={
                    fulfillmentType === fulfillmentTypes.DELIVERY
                      ? t('steps.deliveryMoment.fulfillmentType.pickup.title')
                      : t('steps.deliveryMoment.fulfillmentType.dropoff.title')
                  }
                />
              </div>
            )}

          {!isInRegion && fulfillmentType === fulfillmentTypes.DELIVERY && (
            <Alert
              description={t('donation.outsideDeliveryArea')}
              type="info"
              className="mb-4"
            />
          )}
        </div>

        <Form.Item className="col-span-6" shouldUpdate>
          {() => {
            const fields = form.getFieldsValue();
            const allFilled =
              fields.email &&
              fields.phone &&
              fields.first_name &&
              fields.last_name &&
              fields.address &&
              fields.house_number &&
              fields.postal_code &&
              fields.city &&
              fields.bike_size &&
              fields.quantity != null &&
              fields.quantity !== '' &&
              fields.date;
            return (
              <Button
                type="primary"
                htmlType="submit"
                loading={submitting}
                disabled={!allFilled}
                size="large"
                block>
                {t('donation.submit')}
              </Button>
            );
          }}
        </Form.Item>
      </Form>
    </div>
  );
}
